import type { SvgIconComponent } from '@mui/icons-material';
import Apartment from '@mui/icons-material/Apartment';
import Handshake from '@mui/icons-material/Handshake';
import Hub from '@mui/icons-material/Hub';
import RequestQuote from '@mui/icons-material/RequestQuote';
import Warehouse from '@mui/icons-material/Warehouse';
import { ExportMode, ExportRequestDto, ExportType } from '../../types/export';

export const EXPORT_TYPE_ICONS: Record<ExportType, SvgIconComponent> = {
  [ExportType.FULFILLMENT_CONTEXTS]: Hub,
  [ExportType.TENANTS]: Apartment,
  [ExportType.ORIGINS]: Warehouse,
  [ExportType.CARRIER_CONTRACTS]: Handshake,
  [ExportType.CARRIER_RATES]: RequestQuote,
};

const ITEM_NOUNS: Record<ExportType, string> = {
  [ExportType.FULFILLMENT_CONTEXTS]: 'fulfillment context',
  [ExportType.TENANTS]: 'tenant',
  [ExportType.ORIGINS]: 'origin',
  [ExportType.CARRIER_CONTRACTS]: 'carrier contract',
  [ExportType.CARRIER_RATES]: 'carrier rate',
};

/** Singular noun for one record of an export type, e.g. "fulfillment context". */
export const itemNoun = (type: ExportType | ''): string => (type ? ITEM_NOUNS[type] : 'item');

export type IncludeFlag = {
  [K in keyof ExportRequestDto]-?: K extends `include${string}` ? K : never;
}[keyof ExportRequestDto];

export interface RelatedOption {
  key: IncludeFlag;
  label: string;
  description: string;
  appliesTo: (type: ExportType, mode: ExportMode) => boolean;
}

const isAnyOf =
  (...types: ExportType[]) =>
  (type: ExportType) =>
    types.includes(type);

const FC = ExportType.FULFILLMENT_CONTEXTS;

// Which include* flags apply to which export type; the backend ignores flags outside these combinations.
const RELATED_OPTIONS: RelatedOption[] = [
  { key: 'includeRateCards', label: 'Rate cards', description: 'Negotiated rates per carrier service', appliesTo: isAnyOf(FC, ExportType.CARRIER_CONTRACTS) },
  { key: 'includeThirdPartyBilling', label: 'Third-party billing', description: 'Billing account assignments', appliesTo: isAnyOf(FC, ExportType.TENANTS) },
  { key: 'includeInjectionProfiles', label: 'Injection profiles', description: 'Injection points and schedules', appliesTo: isAnyOf(FC, ExportType.TENANTS) },
  { key: 'includeLanes', label: 'Lanes', description: 'Origin–destination lanes', appliesTo: isAnyOf(FC) },
  { key: 'includeLabelAugmentations', label: 'Label augmentations', description: 'Custom label fields and overrides', appliesTo: isAnyOf(FC, ExportType.TENANTS) },
  { key: 'includeOrigins', label: 'Origins', description: 'Ship-from locations', appliesTo: isAnyOf(FC, ExportType.TENANTS) },
  { key: 'includeCarrierSchedules', label: 'Carrier schedules', description: 'Carrier pickup schedules', appliesTo: isAnyOf(ExportType.CARRIER_CONTRACTS, ExportType.ORIGINS) },
  {
    key: 'includeIncompleteFcs',
    label: 'Incomplete FCs',
    description: 'Fulfillment contexts missing required configuration (single workbook only)',
    appliesTo: (type, mode) => type === FC && mode === ExportMode.CONSOLIDATED,
  },
];

export const relatedOptionsFor = (type: ExportType | '', mode: ExportMode): RelatedOption[] =>
  type ? RELATED_OPTIONS.filter((option) => option.appliesTo(type, mode)) : [];
